import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Users, QrCode, Settings, BarChart3, Shield, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { loginSchema } from '../utils/validation';
import { APP_NAME } from '../utils/constants';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import Footer from '../components/common/Footer';
import logo from '../assets/logo_transparent.png';

const Login = () => {
  const navigate = useNavigate();
  const { login, verifyOtp, isAuthenticated, loading: authLoading, user } = useAuth();
  const [step, setStep] = useState('login'); // 'login' or 'otp'
  const [otpCode, setOtpCode] = useState('');
  const [emailForOtp, setEmailForOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: zodResolver(loginSchema),
  });

  // Pre-fill remembered email and state
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedPreference = localStorage.getItem('rememberMe') === 'true';

    if (rememberedEmail) {
      setValue('email', rememberedEmail);
    }
    setRememberMe(rememberedPreference);
  }, [setValue]);

  // If user is already authenticated, redirect based on role
  React.useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      if (user.role === 'attendee') {
        navigate('/self-register');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      if (step === 'login') {
        console.log('Attempting login with:', data);
        const result = await login(data, rememberMe);
        console.log('Login result:', result);

        if (result.success && result.require_otp) {
          setStep('otp');
          setEmailForOtp(result.email || data.email);
          setLoading(false);
        } else if (!result.success) {
          setError(result.error);
          setLoading(false);
        }
      } else {
        // Verify OTP - logic handled separately in handleVerifyOtp but kept here for structure if needed
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      setLoading(false);
    }
  };

  const lastAttemptedOtp = React.useRef('');

  const handleVerifyOtp = React.useCallback(async (e?: any) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setError('');
      lastAttemptedOtp.current = otpCode; // Record this attempt

      const result = await verifyOtp(emailForOtp, otpCode, rememberMe);

      if (!result.success) {
        setError(result.error);
        setLoading(false);
      }
      // On success, useEffect handles redirect
    } catch (err) {
      setError('Verification failed');
      setLoading(false);
    }
  }, [emailForOtp, otpCode, verifyOtp, rememberMe]);

  // Auto-verify when OTP length is 6
  React.useEffect(() => {
    if (otpCode.length === 6 && step === 'otp' && !loading && otpCode !== lastAttemptedOtp.current) {
      handleVerifyOtp();
    }
  }, [otpCode, step, loading, handleVerifyOtp]);

  const features = [
    {
      icon: Users,
      title: "Easy Attendee Management",
      description: "Keep track of everyone at your events with smart filtering and quick search - no more manual spreadsheets!"
    },
    {
      icon: QrCode,
      title: "Instant Check-Ins",
      description: "Generate custom QR codes that let attendees check in with just their phone - super fast and contactless"
    },
    {
      icon: Settings,
      title: "Flexible Registration Forms",
      description: "Create registration forms that fit your event perfectly - add any fields you need, from dietary restrictions to t-shirt sizes"
    },
    {
      icon: BarChart3,
      title: "Smart Analytics",
      description: "Get insights about your attendees and events with beautiful reports you can export and share"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Keep your data safe with role-based permissions - only the right people see the right information"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Create professional badges and print attendee lists in seconds - everything happens at the speed of your event"
    }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 flex items-center justify-center">
        <Loading fullScreen text="Authenticating..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 flex flex-col">
      <div className="flex flex-1">
        {/* Left Panel - Features (Desktop) */}
        <div className="hidden lg:flex lg:flex-1 bg-white/10 backdrop-blur-sm border-r border-white/20 p-8 xl:p-12">
          <div className="w-full max-w-md mx-auto flex flex-col justify-center">
            {/* Logo Section */}
            <div className="text-center mb-8">
              <div className="inline-flex mb-6">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg border border-white/30">
                  <img src={logo} alt={APP_NAME} className="h-12 w-auto" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
              <p className="text-white/80 text-lg">Sign in to your account</p>
            </div>

            {/* Features Grid */}
            <div className="space-y-6">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 text-center">
              <p className="text-white/90 text-sm">
                Join thousands of event organizers who trust {APP_NAME} for seamless attendee management
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="flex-1 flex flex-col">
          {/* Login Form Section - Mobile First */}
          <div className="flex-1 flex items-center justify-center p-4 lg:p-8 xl:p-12">
            <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl border-0">
              <Card.Content className="py-8 px-6">
                {/* Mobile Logo */}
                <div className="lg:hidden text-center mb-8">
                  <div className="inline-flex mb-4">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg border border-white/30">
                      <img src={logo} alt={APP_NAME} className="h-10 w-auto" />
                    </div>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
                  <p className="text-gray-600 mt-2">Sign in to your account</p>
                </div>

                {/* Desktop Login Header */}
                <div className="hidden lg:block text-center mb-8">
                  <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
                  <p className="text-gray-600 mt-2">Sign in to your account</p>
                </div>

                {step === 'login' ? (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="admin@example.com"
                      error={errors.email?.message as string}
                      {...register('email')}
                    />

                    <div className="relative">
                      <Input
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        error={errors.password?.message as string}
                        {...register('password')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                        />
                        <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                          Remember me
                        </label>
                      </div>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Forgot password?
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full"
                      loading={loading}
                    >
                      Sign In
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-sm mb-4">
                        We sent a verification code to <strong>{emailForOtp}</strong>
                      </div>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        className="w-full text-center text-2xl tracking-[0.5em] font-bold py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        maxLength={6}
                        autoFocus
                      />
                      <p className="mt-2 text-xs text-center text-gray-500">
                        Enter the 6-digit code sent to your email
                      </p>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full"
                      loading={loading}
                      disabled={otpCode.length !== 6}
                    >
                      Verify & Login
                    </Button>

                    <div className="text-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setStep('login');
                          setError('');
                          setOtpCode('');
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Back to Login
                      </button>
                    </div>
                  </form>
                )}

                {/* Demo Credentials - Uncomment for development */}
                {/* <div className="mt-6 text-center text-sm text-gray-600">
                  <p>Demo Credentials:</p>
                  <p className="text-xs mt-1">admin@programtags.com / admin123</p>
                </div> */}
              </Card.Content>
            </Card>
          </div>

          {/* Mobile Features Section - After Login */}
          <div className="lg:hidden bg-white/10 backdrop-blur-sm p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Discover {APP_NAME}</h2>
              <p className="text-white/80 text-sm">Everything you need for professional event management</p>
            </div>

            {/* Mobile Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-white font-medium text-sm">{feature.title}</h3>
                    </div>
                    <p className="text-white/70 text-xs leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Login;
